import type { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import type { BoardSortDirectionValue } from "@design-system/Board/BoardSortDirection";
import { clientsWorkOverviewViewConfig } from "../clientsWorkOverviewViewConfig";
import { DEFAULT_WORK_SORT } from "../workViewSort";

const SORTABLE_COLUMNS = Object.entries(
    clientsWorkOverviewViewConfig.columns,
).flatMap(([id, { name, sort }]) => (sort ? [{ id, name, sort }] : []));

/** The gear's Sort choices: every List column that sorts, named as List names it. */
export const WORK_BOARD_SORT_OPTIONS = SORTABLE_COLUMNS.map(({ id, name }) => ({
    value: id,
    label: name,
}));

interface WorkBoardSort {
    /** The List column sorted by. */
    sort: string;
    sortDirection: BoardSortDirectionValue;
}

const findColumn = (sort: ClientsWorkOverviewSortValue) =>
    SORTABLE_COLUMNS.find(
        (column) => column.sort.asc === sort || column.sort.desc === sort,
    );

/** List's sort as the gear shows it; a sort no column offers shows as the default. */
export const getWorkBoardSort = (
    listSort: ClientsWorkOverviewSortValue,
): WorkBoardSort => {
    const shownSort = findColumn(listSort) ? listSort : DEFAULT_WORK_SORT;
    const column = findColumn(shownSort);
    return {
        sort: column?.id ?? "",
        sortDirection: column?.sort.desc === shownSort ? "desc" : "asc",
    };
};

/**
 * List's sort after a gear change. A new column starts in the direction its
 * List header would on a first click.
 */
export const getWorkSortValue = ({
    change,
    listSort,
}: {
    change: Partial<WorkBoardSort>;
    listSort: ClientsWorkOverviewSortValue;
}): ClientsWorkOverviewSortValue => {
    const current = getWorkBoardSort(listSort);
    const column = SORTABLE_COLUMNS.find(
        ({ id }) => id === (change.sort ?? current.sort),
    );
    if (!column) {
        return listSort;
    }
    const sortDirection =
        change.sortDirection ??
        (column.id === current.sort
            ? current.sortDirection
            : (column.sort.defaultOrder ?? "desc"));
    return column.sort[sortDirection];
};
